import { ref, reactive, computed, nextTick, type Ref, type Reactive } from "vue";
import {
  ScheduledDeliveryCreateInput,
  DeliveryTimeListInput,
  DeliverableAreaDeliveryIdInput,
  ShopListInput,
} from "@/generated/graphql";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
} from "@/common/localStorageUtil";
import { getChainList } from "@/common/graphql/chain";
import { getWordList } from "@/common/graphql/expression";
import { GeneralTypeList } from "@/common/constant";
import scheduledDeliveryStore from "@/store/scheduledDeliveryStore";
import { ApolloQueryResult } from "@apollo/client/core";
import { GetShopQuery } from "@/generated/graphql";
import type { ShopDataType, ListData } from "../types";
import { SCHEDULED_DELIVERY_REGISTER_FILE_NAME } from "../constants";

/**
 * 店舗間定期配達登録 初期化処理
 * 画面初期ロード（onMounted 処理の委譲先）
 */
export const useScheduledDeliveryRegisterInit = (deps: {
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  overlay: Ref<boolean>;
  updateMode: Ref<boolean>;
  deleteMode: Ref<boolean>;
  displayConfirmDialog: Ref<boolean>;
  scheduledDeliveryCreateInput: Reactive<ScheduledDeliveryCreateInput>;
  shopListInput: Reactive<ShopListInput>;
  deliveryTimeListInput: Reactive<DeliveryTimeListInput>;
  deliverableAreaDeliveryIdInput: Reactive<DeliverableAreaDeliveryIdInput>;
  deliveryIdSelectValues: Ref<Array<{ label: string; value: number }> | null | undefined>;
  deliverySelectValues: Ref<ListData | null | undefined>;
  deliveryRequestCreateDateValues: Ref<{ label: string; delivery_request_create_date: number } | null | undefined>;
  deliveryRequestCreateTimeValues: Ref<number | null | undefined>;
  listDeliveryId: Ref<ListData[]>;
  deliveryRequestCreateDateItem: Array<{ label: string; delivery_request_create_date: number }>;
  getShopData: (shop_code: string) => Promise<ApolloQueryResult<GetShopQuery> | undefined>;
  GetAreaData: (shop_code: string, zip_code: string) => Promise<void>;
  queryDeliverytime: () => Promise<void>;
  chkDeliveryId: () => Promise<void | false>;
  getScheduledDelivery: (scheduled_delivery_no: string) => Promise<void | undefined>;
  SetdeliveryId: () => Promise<void>;
  onChangeDeliveryId: () => Promise<void>;
  onChangeDeliveryRequestCreateDate: () => Promise<void>;
}) => {
  const {
    errdialog,
    errMessage,
    overlay,
    updateMode,
    deleteMode,
    displayConfirmDialog,
    scheduledDeliveryCreateInput,
    shopListInput,
    deliveryTimeListInput,
    deliverableAreaDeliveryIdInput,
    deliveryIdSelectValues,
    deliverySelectValues,
    deliveryRequestCreateDateValues,
    deliveryRequestCreateTimeValues,
    listDeliveryId,
    deliveryRequestCreateDateItem,
    getShopData,
    GetAreaData,
    queryDeliverytime,
    chkDeliveryId,
    getScheduledDelivery,
    SetdeliveryId,
    onChangeDeliveryId,
    onChangeDeliveryRequestCreateDate,
  } = deps;

  const wordList = reactive<Record<string, string>>({});
  const requiredFieldLabelKeys = [
    "lbl_delivery_week",
    "lbl_delivery_id",
    "lbl_irregular_delivery_start_time",
    "lbl_irregular_delivery_end_time",
    "lbl_delivery_request_create_date",
    "lbl_delivery_request_create_time",
    "lbl_valid_date_from",
    "lbl_valid_date_to",
    "lbl_delivery_notes",
  ];
  const isWordListReady = computed(() => {
    if (Object.keys(wordList).length === 0) return false;
    return requiredFieldLabelKeys.every((key) => key in wordList);
  });

  /**
   * チェーンリスト
   */
  const chainListItems = ref<
    | (
        | { __typename?: "Chain" | undefined; chain_code: string; name: string }
        | null
        | undefined
      )[]
    | null
    | undefined
  >(null);
  const chain_name = ref<string>("");

  /**
   * 店舗情報
   */
  const shopData = ref<ShopDataType>(null);
  const shopData_from_name = ref<string>("");
  const shopData_from_address = ref<string>("");
  const shopData_from_zip_code = ref<string>("");
  const shopData_to_name = ref<string>("");
  const shopData_to_address = ref<string>("");

  /**
   * 休日設定の選択ボックス
   */
  const deliveryIdItems = ref<Array<{ value: number; label: string }>>([]);

  /**
   * 初期ロード実行
   */
  async function runInitialLoad(): Promise<void> {
    try {
      overlay.value = true;

      //文言データを取得する
      const wl = await getWordList(
        localStorageGetChainCode(),
        SCHEDULED_DELIVERY_REGISTER_FILE_NAME,
        localStorageGetCustomerCode()
      );
      Object.assign(wordList, wl);

      //チェーン情報を取得する
      chainListItems.value = await getChainList(localStorageGetCustomerCode());

      /**
       * 休日設定の選択ボックス
       */
      // GeneralTypeList.week をコピーして利用
      deliveryIdItems.value = [
        ...(GeneralTypeList.week as Array<{ value: number; label: string }>),
      ];
      deliveryIdItems.value.unshift({ label: "全曜日", value: 99 });

      // storeからshopInfoを取得する
      if (scheduledDeliveryStore.hasValue) {
        // モード判定
        if (scheduledDeliveryStore.processFlag === 1) {
          updateMode.value = true;
        } else if (scheduledDeliveryStore.processFlag === 2) {
          deleteMode.value = true;
          // 店舗登録確認ダイアログ 表示
          displayConfirmDialog.value = true;
        }

        // チェーンコードを設定
        shopListInput.chain_code = scheduledDeliveryStore.chain_code;
        deliveryTimeListInput.chain_code = scheduledDeliveryStore.chain_code;
        scheduledDeliveryCreateInput.chain_code =
          scheduledDeliveryStore.chain_code;

        // チェーン名取得
        chainListItems.value?.forEach((value) => {
          if (
            value !== undefined &&
            value !== null &&
            value.chain_code !== undefined &&
            value.chain_code !== null &&
            value.name !== undefined &&
            value.name !== null
          ) {
            if (value.chain_code == scheduledDeliveryCreateInput.chain_code) {
              chain_name.value = value.name;
            }
          }
        });

        // 出荷元
        scheduledDeliveryCreateInput.shop_code =
          scheduledDeliveryStore.shop_code_from;
        deliveryTimeListInput.shop_code = scheduledDeliveryStore.shop_code_from;

        // 店舗情報取得
        const shop_data_from = await getShopData(
          scheduledDeliveryCreateInput.shop_code
        );

        shopData.value = undefined;
        shopData.value = shop_data_from!.data!.getShop!;
        shopData_from_name.value = shopData.value!.name;
        shopData_from_address.value =
          "〒" +
          shopData.value!.zip_code +
          "\r\n" +
          shopData.value!.address1 +
          " " +
          shopData.value!.address2;
        if (
          shopData.value!.address3 !== undefined &&
          shopData.value!.address3 !== null &&
          shopData.value!.address3 !== ""
        ) {
          shopData_from_address.value += " " + shopData.value!.address3;
        }
        shopData_from_zip_code.value = shopData.value!.zip_code;

        // 配達先
        scheduledDeliveryCreateInput.delivery_shop_code =
          scheduledDeliveryStore.shop_code_to;

        // 店舗情報取得
        const shop_data_to = await getShopData(
          scheduledDeliveryCreateInput.delivery_shop_code
        );

        shopData.value = undefined;
        shopData.value = shop_data_to!.data.getShop!;
        shopData_to_name.value = shopData.value!.name;
        shopData_to_address.value =
          "〒" +
          shopData.value!.zip_code +
          "\r\n" +
          shopData.value!.address1 +
          " " +
          shopData.value!.address2;
        if (
          shopData.value!.address3 !== undefined &&
          shopData.value!.address3 !== null &&
          shopData.value!.address3 !== ""
        ) {
          shopData_to_address.value += " " + shopData.value!.address3;
        }
        if (
          shopData.value!.phone !== undefined &&
          shopData.value!.phone !== null &&
          shopData.value!.phone !== ""
        ) {
          shopData_to_address.value += "\r\n" + shopData.value!.phone;
        }
        deliverableAreaDeliveryIdInput.delivered_zip_code =
          shopData.value!.zip_code;

        // 配達エリア取得
        await GetAreaData(
          scheduledDeliveryCreateInput.shop_code,
          deliverableAreaDeliveryIdInput.delivered_zip_code
        );

        // 配達時間一覧
        await queryDeliverytime();

        // エリア名取得
        await chkDeliveryId();

        // 変更・削除時、値セット
        if (updateMode.value || deleteMode.value) {
          // 店舗間定期配達情報取得
          await getScheduledDelivery(
            scheduledDeliveryStore.scheduled_delivery_no
          );

          // お届け便選択の曜日
          await SetdeliveryId();

          const irregular_delivery_start_time =
            scheduledDeliveryCreateInput.irregular_delivery_start_time;
          const irregular_delivery_end_time =
            scheduledDeliveryCreateInput.irregular_delivery_end_time;

          // お届け便名取得
          // Vuetify 3のv-autocompleteでreturn-objectを使用する場合、
          // 選択された値がitemsの中のオブジェクトと同じ参照である必要がある
          // nextTick()を使用してDOMの更新を待ってから値を設定する
          await nextTick();
          const matchedDelivery = listDeliveryId.value!.find((value) => {
            if (
              value !== undefined &&
              value !== null &&
              (value as ListData).delivery_id !== undefined &&
              (value as ListData).delivery_id !== null
            ) {
              return (
                value.area_code == scheduledDeliveryCreateInput.area_code &&
                value.delivery_id == scheduledDeliveryCreateInput.delivery_id
              );
            }
            return false;
          });
          if (matchedDelivery) {
            // listDeliveryIdの中のオブジェクトをそのまま使用
            deliverySelectValues.value = matchedDelivery;
          }

          // お届け便選択処理
          await onChangeDeliveryId();

          scheduledDeliveryCreateInput.irregular_delivery_start_time =
            irregular_delivery_start_time;
          scheduledDeliveryCreateInput.irregular_delivery_end_time =
            irregular_delivery_end_time;

          // 配達依頼作成日取得
          deliveryRequestCreateDateItem!.forEach((value) => {
            if (
              value !== undefined &&
              value !== null &&
              value.delivery_request_create_date !== undefined &&
              value.delivery_request_create_date !== null
            ) {
              if (
                value.delivery_request_create_date ==
                scheduledDeliveryCreateInput.delivery_request_create_date
              ) {
                deliveryRequestCreateDateValues.value = {
                  delivery_request_create_date:
                    value.delivery_request_create_date,
                  label: value.label ? value.label : "",
                };
              }
            }
          });

          // 配達依頼作成時間作成
          await onChangeDeliveryRequestCreateDate();

          // 配達依頼作成時間取得
          deliveryRequestCreateTimeValues.value = Number(
            scheduledDeliveryCreateInput.delivery_request_create_time.substring(
              0,
              2
            )
          );
        }
      }
    } catch (err: unknown) {
      errdialog.value = true;
      if (err instanceof Error) {
        errMessage.value = err.message;
      } else {
        errMessage.value = "不明なエラーが発生しました。";
      }
      return;
    } finally {
      overlay.value = false;
    }
  }

  return {
    wordList,
    isWordListReady,
    chainListItems,
    chain_name,
    shopData,
    shopData_from_name,
    shopData_from_address,
    shopData_from_zip_code,
    shopData_to_name,
    shopData_to_address,
    deliveryIdItems,
    runInitialLoad,
  };
};
